import React, { useCallback, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { toast } from 'react-toastify';

import DocumentMetadataForm from 'components/Documents/DocumentMetadataForm';
import { documentsActionButtonSx } from 'components/Documents/documentsLayout';
import { ACCEPTED_UPLOAD_ACCEPT_ATTR } from 'components/Documents/Scanner/constants';
import type { DocumentMetadataValues } from 'components/Documents/Scanner/types';
import { isAcceptedUploadFile } from 'utils/imageUtils';

interface FileUploadWorkflowProps {
  patientId: string;
  onUpload: (payload: { documentName: string; documentDescription?: string; sourceFile: File }) => Promise<void>;
  onCancel: () => void;
}

const FileUploadWorkflow: React.FC<FileUploadWorkflowProps> = ({ patientId, onUpload, onCancel }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<DocumentMetadataValues>({
    documentName: '',
    documentDescription: '',
  });
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!isAcceptedUploadFile(file)) {
      setFileError('Invalid file type. Allowed: JPG, PNG, PDF, DOC, DOCX.');
      setSelectedFile(null);
      return;
    }

    setFileError(null);
    setSelectedFile(file);
    setMetadata((current) => ({
      ...current,
      documentName: current.documentName ?? file.name.replace(/\.[^.]+$/, ''),
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedFile) {
      toast('Please select a file to upload.', { type: 'info' });
      return;
    }

    if (!metadata.documentName.trim()) {
      toast('Document name is required.', { type: 'error' });
      return;
    }

    if (!patientId) {
      toast('Patient information is missing.', { type: 'error' });
      return;
    }

    setIsUploading(true);
    try {
      await onUpload({
        documentName: metadata.documentName.trim(),
        documentDescription: metadata.documentDescription.trim() ?? undefined,
        sourceFile: selectedFile,
      });
      setSelectedFile(null);
      onCancel();
    } catch (error) {
      console.error('File upload failed:', error);
      toast('Document upload failed. Please try again.', { type: 'error' });
    } finally {
      setIsUploading(false);
    }
  }, [metadata, onCancel, onUpload, patientId, selectedFile]);

  return (
    <Stack spacing={2}>
      <input ref={inputRef} type='file' accept={ACCEPTED_UPLOAD_ACCEPT_ATTR} hidden onChange={handleFileChange} />

      <Button
        variant='outlined'
        startIcon={<CloudUploadIcon />}
        onClick={() => inputRef.current?.click()}
        sx={documentsActionButtonSx}
      >
        Choose file
      </Button>

      {selectedFile && (
        <Alert severity='info'>
          Selected: <strong>{selectedFile.name}</strong> ({Math.round(selectedFile.size / 1024)} KB)
        </Alert>
      )}

      {fileError && <Alert severity='error'>{fileError}</Alert>}

      {selectedFile && (
        <Box>
          <Typography variant='subtitle1' fontWeight={600} gutterBottom>
            Document Details
          </Typography>
          <DocumentMetadataForm values={metadata} onChange={setMetadata} disabled={isUploading} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 2,
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems='center' sx={{ mt: 2 }}>
              <Button
                variant='contained'
                color='success'
                sx={{
                  ...documentsActionButtonSx,
                  mt: 0,
                  width: { xs: '100%', md: 'auto' },
                }}
                disabled={isUploading ?? !metadata.documentName.trim()}
                onClick={handleSubmit}
              >
                {isUploading ? <CircularProgress size={22} color='inherit' /> : 'Upload document'}
              </Button>

              <Button
                variant='text'
                onClick={onCancel}
                disabled={isUploading}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default FileUploadWorkflow;
